<script setup lang="ts">
const title = 'Kontakt – Web Professionals'
const description = 'Web Professionals GmbH in Grabs SG. Kontaktdaten, Anreise und wie du uns am besten erreichst.'

useSeoMeta({
  title,
  ogTitle: title,
  description,
  ogDescription: description
})

const address = {
  company: 'Web Professionals GmbH',
  street: 'Staudnerbachstrasse 8',
  zipCity: 'CH-9472 Grabs',
  phone: '081 756 75 76',
  phoneHref: 'tel:+41817567576',
  email: 'hello@web-professionals.ch',
  emailHref: 'mailto:hello@web-professionals.ch',
  mapsHref: 'https://www.google.com/maps/search/?api=1&query=Staudnerbachstrasse+8%2C+9472+Grabs'
}

const toast = useToast()
const email = ref('')
const loading = ref(false)

async function onSubmit() {
  loading.value = true
  try {
    const res = await fetch('/api/newsletter', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: String(email.value) })
    })
    if (!res.ok) {
      const data = await res.json() as { statusMessage?: string }
      email.value = ''
      toast.add({
        title: 'Fehler',
        description: data.statusMessage || 'Anmeldung fehlgeschlagen. Bitte versuche es später erneut.',
        color: 'error'
      })
      return
    }
    email.value = ''
    toast.add({
      title: 'Angemeldet!',
      description: 'Du hast dich erfolgreich für unseren Newsletter angemeldet.',
      color: 'success'
    })
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : undefined
    toast.add({
      title: 'Fehler',
      description: msg || 'Anmeldung fehlgeschlagen. Bitte versuche es später erneut.',
      color: 'error'
    })
  } finally {
    loading.value = false
  }
}
</script>

<template>
  <div>
    <UPageHero
      title="Hallo Web Professionals!"
      description="Wo du uns findest. Zum Lernen, Quatschen, Kaffeetrinken …"
    >
      <template #top>
        <HeroBackground />
      </template>
    </UPageHero>

    <UPageSection>
      <UContainer>
        <div class="grid gap-6 md:grid-cols-2">
          <UCard>
            <template #header>
              <div class="flex items-center gap-2">
                <UIcon
                  name="i-lucide-map-pin"
                  class="text-primary text-xl shrink-0"
                />
                <h2 class="text-lg font-semibold">
                  Adresse
                </h2>
              </div>
            </template>

            <address class="not-italic text-muted">
              <p class="font-semibold text-default">
                {{ address.company }}
              </p>
              <p>{{ address.street }}</p>
              <p>{{ address.zipCity }}</p>
            </address>

            <template #footer>
              <UButton
                :to="address.mapsHref"
                target="_blank"
                rel="noopener"
                icon="i-lucide-map"
                variant="subtle"
                size="sm"
                label="In Karte öffnen"
              />
            </template>
          </UCard>

          <UCard>
            <template #header>
              <div class="flex items-center gap-2">
                <UIcon
                  name="i-lucide-mail"
                  class="text-primary text-xl shrink-0"
                />
                <h2 class="text-lg font-semibold">
                  Direkt erreichen
                </h2>
              </div>
            </template>

            <dl class="grid gap-x-6 gap-y-3 grid-cols-[max-content_1fr] text-sm">
              <dt class="font-semibold text-default flex items-center gap-2">
                <UIcon
                  name="i-lucide-mail"
                  class="shrink-0"
                />
                E-Mail
              </dt>
              <dd>
                <a
                  :href="address.emailHref"
                  class="text-primary hover:underline"
                >{{ address.email }}</a>
              </dd>

              <dt class="font-semibold text-default flex items-center gap-2">
                <UIcon
                  name="i-lucide-phone"
                  class="shrink-0"
                />
                Telefon
              </dt>
              <dd>
                <a
                  :href="address.phoneHref"
                  class="text-muted hover:text-primary"
                >{{ address.phone }}</a>
              </dd>
            </dl>

            <p class="text-sm text-muted mt-4">
              Da wir nebst unserer Tätigkeit als Dozenten auch noch anderweitigen Verpflichtungen
              nachgehen, sind wir nicht immer telefonisch erreichbar. Eine E-Mail ist erfahrungsgemäss
              die schnellste Art, uns zu erreichen.
            </p>
          </UCard>

          <UCard>
            <template #header>
              <div class="flex items-center gap-2">
                <UIcon
                  name="i-simple-icons-whatsapp"
                  class="text-[#25D366] text-xl shrink-0"
                />
                <h2 class="text-lg font-semibold">
                  WhatsApp Community
                </h2>
              </div>
            </template>

            <div class="flex flex-col sm:flex-row gap-6 items-start">
              <NuxtImg
                src="/whatsapp_qr.svg"
                alt="QR Code – WhatsApp Community Web Professionals"
                class="w-32 h-32 shrink-0"
              />
              <div class="text-sm text-muted">
                <p class="mb-3">
                  Tritt unserer WhatsApp Community bei und bleib auf dem Laufenden
                  über Events, neue Kurstermine und Neuigkeiten aus der Web-Szene.
                </p>
                <UButton
                  to="https://chat.whatsapp.com/IuBcKFBjRnYJtqAMzP72T5"
                  target="_blank"
                  rel="noopener"
                  icon="i-simple-icons-whatsapp"
                  color="success"
                  variant="subtle"
                  size="sm"
                  label="Community beitreten"
                />
              </div>
            </div>
          </UCard>

          <UCard>
            <template #header>
              <div class="flex items-center gap-2">
                <UIcon
                  name="i-lucide-mail"
                  class="text-primary text-xl shrink-0"
                />
                <h2 class="text-lg font-semibold">
                  Newsletter
                </h2>
              </div>
            </template>

            <p class="text-sm text-muted mb-4">
              Kursneuigkeiten, Events und Tipps direkt in dein Postfach –
              kein Spam, jederzeit abbestellbar.
            </p>

            <form @submit.prevent="onSubmit">
              <UFormField
                name="email"
                label="E-Mail-Adresse"
                size="lg"
              >
                <UInput
                  v-model="email"
                  type="email"
                  class="w-full"
                  placeholder="deine@email.ch"
                >
                  <template #trailing>
                    <UButton
                      type="submit"
                      size="xs"
                      color="neutral"
                      label="Anmelden"
                      :loading="loading"
                    />
                  </template>
                </UInput>
              </UFormField>
            </form>
          </UCard>

          <UCard class="md:col-span-2">
            <template #header>
              <div class="flex items-center gap-2">
                <UIcon
                  name="i-lucide-route"
                  class="text-primary text-xl shrink-0"
                />
                <h2 class="text-lg font-semibold">
                  Anreise
                </h2>
              </div>
            </template>

            <div class="grid gap-6 sm:grid-cols-2 text-sm">
              <div class="flex items-start gap-3">
                <UIcon
                  name="i-lucide-bus"
                  class="text-primary text-xl shrink-0 mt-0.5"
                />
                <div>
                  <p class="font-semibold text-default mb-1">
                    Mit dem ÖV
                  </p>
                  <p class="text-muted">
                    Postauto-Haltestelle «Hochhaus», ca. 2 Minuten zu Fuss.
                  </p>
                </div>
              </div>

              <div class="flex items-start gap-3">
                <UIcon
                  name="i-lucide-square-parking"
                  class="text-primary text-xl shrink-0 mt-0.5"
                />
                <div>
                  <p class="font-semibold text-default mb-1">
                    Mit dem Auto
                  </p>
                  <p class="text-muted">
                    Parkplatz bei der Evangelischen Kirche Grabs, ca. 5 Minuten zu Fuss.
                  </p>
                </div>
              </div>
            </div>
          </UCard>
        </div>
      </UContainer>
    </UPageSection>
  </div>
</template>
